import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { FileRoutesByTo } from '@school-manage/routeTree.gen';

import {
  faHome,
  faListCheck,
  faNewspaper,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@heroui/react';
import { NAVIGATION_BAR_HEIGHT } from '@school-manage/common/constants';
import { useScrollVisibility } from '@school-manage/hooks/useScrollVisibility';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThrottledCallback } from 'use-debounce';

interface NavigateLink {
  icon: IconDefinition;
  path: keyof FileRoutesByTo;
  text: string;
}

export function NavigationBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { opacity } = useScrollVisibility({
    hasOpacity: true,
    hiddenOpacity: 0.3,
  });

  const navigateLinks = useMemo(() => {
    return [
      {
        icon: faHome,
        path: '/home',
        text: t('home'),
      },
      {
        icon: faListCheck,
        path: '/task',
        text: t('tasks'),
      },
      {
        icon: faNewspaper,
        path: '/news',
        text: t('news'),
      },
      {
        icon: faUser,
        path: '/profile',
        text: t('profile'),
      },
    ] satisfies NavigateLink[];
  }, [t]);

  const handleOnClick = useThrottledCallback(
    async (path: keyof FileRoutesByTo) => {
      navigate({ to: path });
    },
    300
  );

  return (
    <motion.nav
      className="border-default-200 bg-default-50 fixed bottom-0 left-1/2 z-50 w-full max-w-[512px] -translate-x-1/2 overflow-hidden border-t transition-[background-color,border-color,opacity] duration-300 md:rounded-se-2xl md:rounded-ss-2xl md:border"
      style={{ height: NAVIGATION_BAR_HEIGHT, opacity }}
    >
      <div className="mx-auto grid h-full grid-cols-4">
        {navigateLinks.map((link, index) => (
          <div
            className="active:bg-default-100 group inline-flex flex-col items-center justify-center rounded-md px-[20px] pb-[10px] transition-[background-color,scale,transform] duration-300 active:scale-[0.97]"
            key={link.text}
            onClick={() => handleOnClick(link.path)}
            onKeyUp={() => handleOnClick(link.path)}
            role="button"
            tabIndex={index}
          >
            <FontAwesomeIcon
              className={cn('text-default-500 text-lg duration-300', {
                'text-primary-500 text-xl': pathname === link.path,
              })}
              icon={link.icon}
            />
            <span
              className={cn('text-default-500 mt-1 text-xs duration-300', {
                'text-primary-500': pathname === link.path,
              })}
            >
              {link.text}
            </span>
          </div>
        ))}
      </div>
    </motion.nav>
  );
}
